import {
    ApplicationRef,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Inject,
    OnDestroy,
    OnInit,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import {CommunicationService} from '../../../service/communication.service';
import {DialogViewModelCreatorService} from '../../../service/dialog-view-model-creator.service';
import {BotatoChatManagerService} from '../../../service/botato-chat-manager.service';
import {BotatoConfig} from '../../../botato.config';
import {InputComponent} from '../input.component';
import {UploadErrors} from './upload.errors';
import {UploadPreviewFile} from './upload-preview.file';
import {FileUploadFile} from './file-upload-file';
import {ClientFileMessage} from '../../../service/client-file.message';
import {ImageRotater} from './ImageRotater';
import {firstValueFrom, Subscription} from 'rxjs';
import {HttpClient} from '@angular/common/http';
import {
    DialogConfigInputOptionsFileUpload, DialogConfigInputType
} from 'botato-botengine-browser/botato/botato-botengine/source/Core/DialogConfig/DialogConfig.js';

@Component({
    templateUrl: './file-upload.component.html',
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class FileUploadComponent extends InputComponent<DialogConfigInputOptionsFileUpload> implements OnInit, OnDestroy {

    public static TYPE: DialogConfigInputType = 'fileupload';
    public errorMessages: { file: File, message: string, detail?: Error }[] = [];
    public isConnected: boolean = true;
    public isUploading: boolean = false;
    public isDragActive: boolean | undefined;
    public previewFiles: UploadPreviewFile[] = [];
    @ViewChild('fileInput')
    private fileInput!: ElementRef;
    private subscription = new Subscription();

    constructor(private communicationService: CommunicationService,
                private dialogViewModelCreator: DialogViewModelCreatorService,
                private chatManagerService: BotatoChatManagerService,
                private http: HttpClient,
                private application: ApplicationRef,
                private changeDetectorRef: ChangeDetectorRef,
                @Inject('BOTATO_CONFIG') private botatoConfig: BotatoConfig) {
        super();
    }

    public ngOnInit(): void {
        this.subscription.add(this.communicationService.connectionStateChanged.subscribe((state: boolean) => {
            this.isConnected = state;
            this.changeDetectorRef.markForCheck();
        }));
    }

    public ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    public clearErrors(): void {
        this.errorMessages = [];
        this.application.tick();
    }

    public async uploadFiles(): Promise<void> {
        const botatoFiles: FileUploadFile[] = [];

        const uploadFile = new ClientFileMessage(this.previewFiles, true);

        this.dialogViewModelCreator.addUserFileMessage(uploadFile);
        this.isUploading = true;
        this.application.tick();

        for (const previewFile of this.previewFiles) {

            const file = new FileUploadFile();
            file.name = previewFile.name;
            file.content = previewFile.data.replace(/^data:\w+\/\w+;base64,/, '');

            if (this.botatoConfig.fileUploadEnableRestUpload) {
                try {
                    // eslint-disable-next-line no-await-in-loop
                    const fileReference = await firstValueFrom(this.http.post(this.botatoConfig.fileUploadUrl, file)) as FileUploadFile;
                    botatoFiles.push(fileReference);
                } catch (e) {
                    console.error(`Upload failed - Is the upload-api('${this.botatoConfig.fileUploadUrl}') up and running? `);
                    this.addError(previewFile.file, UploadErrors.FILE_UNKNOWN);

                    uploadFile.isError = true;
                    this.isUploading = false;
                    this.changeDetectorRef.markForCheck();
                    this.application.tick();
                    return;
                }
            } else {
                botatoFiles.push(file);
            }
        }

        uploadFile.isUploading = false;
        await this.communicationService.sendEvent(this.requireInputOptions().eventName, 'fileupload', {files: botatoFiles},);

        this.isUploading = false;
        this.previewFiles = [];
        this.changeDetectorRef.markForCheck();
        this.application.tick();
    }

    // eslint-disable-next-line
    public dragOver($event: any): void {

        $event.preventDefault();
        this.isDragActive = !this.isUploading;
        this.application.tick();
    }

    public dragLeave(): void {
        this.isDragActive = false;
        this.application.tick();
    }

// eslint-disable-next-line
    public async filesDroped(dropedFiles: any): Promise<void> {

        dropedFiles.preventDefault();
        this.isDragActive = false;

        if (!this.isUploading) {

            await this.filesSelected(dropedFiles.dataTransfer.files);
        }
    }

    public async filesSelected(fileList: FileList | null): Promise<void> {
        const rawFiles = Array.from(fileList ?? []);

        for (const file of rawFiles) {
            try {
                // eslint-disable-next-line no-await-in-loop
                const fileContent = await this.readFile(file);
                const uploadFile = new UploadPreviewFile(Math.random(), file, file.name, fileContent);
                this.previewFiles.push(uploadFile);
            } catch (e) {
                if (e instanceof Error) {
                    console.log(e);
                    this.addError(file, UploadErrors.FILE_UNKNOWN, e);
                } else {

                    this.addError(file, String(e));
                }
            }
        }


        this.fileInput.nativeElement.value = '';
        this.changeDetectorRef.markForCheck();
        this.application.tick();
    }

    public canUploadFiles(): boolean {
        return this.previewFiles.length > 0 && this.isConnected && !this.isUploading;
    }

    public deleteFile(id: number): void {
        this.previewFiles = this.previewFiles.filter(file => file.id !== id);
        this.application.tick();
    }

    private addError(file: File, message: string, detail?: Error): void {
        this.errorMessages.push({file, message, detail});
        // Also reached from the awaited upload and file-read paths, outside any event handler
        this.changeDetectorRef.markForCheck();
        this.application.tick();
    }

    private async readFile(file: File): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
            reader.onload = (e) => {
                // eslint-disable-next-line
                const base64EncodedData: string = (e.target as any).result;
                // When maxFileSize is 0, then we allow all fileSizes
                const maxFileSize = this.requireInputOptions().maxFileSize;
                if (maxFileSize > 0 && e.total > maxFileSize) {
                    // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
                    reject(UploadErrors.FILE_SIZE);
                } else if (!base64EncodedData.startsWith('data:image')) {
                    // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
                    reject(UploadErrors.FILE_TYPE);
                } else {
                    resolve(ImageRotater.rotateImage(base64EncodedData));
                }
            };
            // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
            reader.onerror = (e) => {
                // When maxFileSize is 0, then we allow all fileSizes
                const maxFileSize = this.requireInputOptions().maxFileSize;
                if (maxFileSize > 0 && e.total > maxFileSize) {
                    // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
                    reject(UploadErrors.FILE_SIZE);
                } else {
                    // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
                    reject(UploadErrors.FILE_UNKNOWN);
                }
            };

            reader.readAsDataURL(file);
        });
    }


}

